import { DashboardOutlined, FileTextOutlined } from '@ant-design/icons';
import { Tag, Modal } from 'antd';
import { Link } from 'components/mvp-typography';
import { APIClinic } from 'lib/api/firebase/apis/api-clinic-balance';
import { clinicActionEditPatientDetails } from 'lib/api/firebase/apis/apis';
import { useGetPrincipalUser } from 'lib/hooks/tanstack/use-getClinicInfo';
import { useAppSelector } from 'lib/store/rootReducer';
import { showRecordUnavailableModal } from 'lib/utils/modal-utils';
import { stringOrNull } from 'lib/utils/string/normalise';
import { menuIconStyle } from 'navigation/menu/menu-icon-style';
import { useMemo } from 'react';
import { paths } from 'routes';
import {
  ActionState,
  ActionTagSettings
} from '../../../../../../../components/action-tag/action-tag-component';
import { useCopyToClipboardMutation } from '../../../../../../../lib/hooks/tanstack/use-CopyToClipboardMutation';
import { ACTION_COPYINVITELINK } from '../../configs/config-action-copyinvitelink';
import { BASE_ACTIONS } from '../../configs/shared-actions-baseconfig';
import { TableRow } from '../../user-table-types';
import { ActionTagEdit } from '../components/action-edit';
import {
  ACTION_SENDSMSINVITE_ONCLICK,
  ActionSendSMSInvite
} from '../components/action-send-sms-invite';
import { PatientRecord } from '../types';

interface RemovePatientActionProps {
  record: TableRow<PatientRecord>;
  onConfirm: () => void;
  isLoading: boolean;
  tagStyles: any;
}

function RemovePatientAction({
  record,
  onConfirm,
  isLoading,
  tagStyles
}: RemovePatientActionProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Remove User From Organisation',
      content: (
        <div>
          <p>
            Are you sure you want to remove {record.firstName} {record.lastName}?
          </p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        onConfirm();
      }
    });
  };

  return (
    <Tag
      {...tagStyles}
      color={isLoading ? tagStyles.color : 'error'}
      className="clickable"
      style={{ cursor: 'pointer', padding: '0.1rem', width: '100%', textAlign: 'center' }}
      onClick={handleClick}
    >
      {isLoading ? 'Removing...' : 'Remove User From Organisation'}
    </Tag>
  );
}

/**
 * Hook that resolves the action menu for a patient record.
 *
 * @param clinicId - The identifier of the clinic in context.
 * @returns An array of the available actions for each patient row.
 */
export function useResolvePatientTableActions(clinicId: string) {
  const copyToClipboardMutation = useCopyToClipboardMutation();
  const currentUser = useAppSelector(state => state.auth.user);
  const principalUserQuery = useGetPrincipalUser(clinicId);

  return useMemo<ActionTagSettings<TableRow<PatientRecord>>[]>(
    () => [
      /**
       * Include base table actions
       */
      ...(BASE_ACTIONS as unknown as ActionTagSettings<
        TableRow<PatientRecord>
      >[]),
      /**
       * Send or resent a SMS invite for a pending invitation
       */
      {
        name: 'Send SMS invite',
        shouldShow: p => p.invitePending,
        initialState: () => ActionState.NEUTRAL,
        render: _props => (
          <ActionSendSMSInvite clinicId={clinicId} {..._props} />
        ),
        onClick: ACTION_SENDSMSINVITE_ONCLICK(clinicId),
        onSuccessSettings: {
          blockOnDone: false,
          timeout: 1000
        },
        onSuccess: refetch => refetch()
      },
      {
        name: 'Generate Assessment Link',
        onClick: () => !!0,
        // TODO: Enable this functionality
        shouldShow: false,
        onSuccess: refetch => refetch(),
        toolTip:
          'Click to generate a link that allows the test-taker to log in and complete their ConcussionRx Assessment with no login'
      },
      /**
       * Edit Test-taker details
       */
      {
        name: 'Edit',
        initialState: () => ActionState.NEUTRAL,
        render: _props => <ActionTagEdit clinicId={clinicId} {..._props} />,
        /**
         *
         * TODO: Add type definition to `extraData`
         * @param p
         * @param extraData
         * @returns
         */
        onClick: (p, extraData) => {
          if (!p.raw.id) {
            throw new Error('ID is undefined. Please try reloading the page');
          }

          return clinicActionEditPatientDetails({
            patientId: p.raw.id,
            firstName: extraData.firstName,
            lastName: extraData.lastName,
            email: extraData.email,
            /**
             * Convert empty strings to null
             */
            phoneNumber: stringOrNull(extraData.phoneNumber),
            clinicianId: extraData.clinicianId,
            assessmentType: extraData.assessmentType,
            clinicId: clinicId
          });
        },
        onSuccessSettings: {
          blockOnDone: false,
          timeout: 1000
        },
        onSuccess: refetch => refetch()
      },
      {
        name: 'Go to user dashboard',
        render: p => {
          const content = (
            <Tag icon={<DashboardOutlined style={menuIconStyle} />}>
              <Link
                target="_current"
                href={
                  p.record.invitePending
                    ? undefined
                    : paths.patient.home(p.record.id)
                }
                style={{ textDecoration: 'none' }}
              >
                Go to Dashboard
              </Link>
            </Tag>
          );

          if (p.record.invitePending) {
            return (
              <div
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  showRecordUnavailableModal();
                }}
              >
                {content}
              </div>
            );
          }

          return content;
        },
        onClick: () => !!0
      },
      {
        name: 'Manage user assessments',
        render: p => {
          const content = (
            <Tag icon={<FileTextOutlined style={menuIconStyle} />}>
              <Link
                target="_current"
                href={
                  p.record.invitePending
                    ? undefined
                    : paths.patient.assessments(p.record.id)
                }
                style={{ textDecoration: 'none' }}
              >
                Manage Assessments
              </Link>
            </Tag>
          );

          if (p.record.invitePending) {
            return (
              <div
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  showRecordUnavailableModal();
                }}
              >
                {content}
              </div>
            );
          }
          return content;
        },
        onClick: () => !!0
      },
      /**
       * Include the copy invite link action
       */
      ACTION_COPYINVITELINK(copyToClipboardMutation.mutateAsync),
      {
        name: 'Remove User From Organisation',
        initialState: () => ActionState.NEUTRAL,
        shouldShow: () => {
          const isCurrentUserPrincipal =
            principalUserQuery.data?.data?.uid === currentUser?.uid;
          return !!isCurrentUserPrincipal;
        },
        render: _props => (
          <RemovePatientAction
            record={_props.record}
            onConfirm={() => _props.onClick(_props.record)}
            isLoading={_props.disabled}
            tagStyles={_props.tagStyles}
          />
        ),
        onClick: async record => {
          if (!record.raw.id) {
            throw new Error('ID is undefined. Please try reloading the page');
          }
          if (record.invitePending === true) {
            await APIClinic.removePatientInvite({
              clinicId,
              invitationId: record.raw.id
            })
            return
          }
          
          await APIClinic.removePatient({
              clinicId,
              patientId: record.raw.id,
            });
        },
        onSuccess: refetch => refetch()
      },
    ],
    [
      clinicId,
      copyToClipboardMutation.mutateAsync,
      currentUser?.uid,
      principalUserQuery.data?.data?.uid
    ]
  );
}
